<template>
	<v-form ref="formRequisitos" fast-fail :disabled="formDesabilitado || loading" @submit.prevent="salvarRequisitos">
		<v-row>
			<v-col cols="12" md="6">
				<div class="mb-2 mr-6"><span class="required">* </span>Experiência (meses)</div>
				<v-combobox v-model="form.experiencia" :items="listaExperiencia" persistent-hint hint="Máximo 6 meses de experiência (Art. 442-A da CLT)" item-title="descricao" item-value="codigo" :rules="validarRequisitos.experienciaMeses" />
			</v-col>
			<v-col cols="12" md="6">
				<div class="mt-2 mr-6"><span class="required">* </span>Comprovação na CTPS?</div>
				<v-radio-group inline v-model="form.comprovacaoCTPS" :rules="validarRequisitos.comprovacaoCTPS">
					<v-radio v-for="item in listaComprovacaoCTPS" :key="item.value" :label="item.label" :value="item.value" />
				</v-radio-group>
			</v-col>
			<v-col cols="12" md="6">
				<div class="mb-2 mr-6"><span class="required">* </span>Escolaridade Mínima</div>
				<v-combobox v-model="form.escolaridadeMinima" :items="listaEscolaridade" item-title="descricao" item-value="codigo" :rules="validarRequisitos.escolaridadeMinima"/>
			</v-col>
			<v-col cols="12" md="6">
				<div class="mb-2 mr-6"><span class="required">* </span>CNH que o trabalhador deve possuir</div>
				<v-combobox v-model="form.tipoHabilitacao" :items="listaHabilitacoes" item-title="descricao" item-value="codigo" :rules="validarRequisitos.tipoHabilitacao"/>
			</v-col>
			<v-col cols="12" md="12">
				<div class="mb-2 mr-6"><span class="required">* </span>Observações Gerais</div>
				<v-textarea v-model="form.observacoes" persistent-hint :hint="hintObservacoes" :counter="300" :rules="validarRequisitos.observacoes"/>
			</v-col>
		</v-row>
		<v-row v-if="!escondeBotoes">
			<v-col cols="6" align="start">
				<v-btn :color="corTexto" variant="outlined" :loading="loading" @click="voltarPasso">
					VOLTAR
				</v-btn>
			</v-col>
			<v-col cols="6" align="end">
				<v-btn color="primary" type="submit" :loading="loading" :disabled="desabilitaAvancar">
					{{ modoSolicitacao ? "SALVAR" : "AVANÇAR" }}
				</v-btn>
			</v-col>
		</v-row>
	</v-form>
</template>
<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { validarRequisitos } from '@/utils/ValidacoesUtils';
import { ConfirmacaoAlert } from "@/utils/AlertUtils.js";
import { useRoute, useRouter } from 'vue-router';
import { listarEscolaridade, listarExperiencias, listarHabilitacoes } from '@/services/ConsultaEntidadeService';
import { vagasEnum } from '@/utils/VagaUtils';
import { cadastrarVaga, editarVaga } from '@/services/VagaService';
import { useStore } from 'vuex';
import { ErroAlert } from "@/utils/AlertUtils.js";
import { vuetify } from '@/plugins/vuetify'

const store = useStore();
const route = useRoute();
const emit = defineEmits(["passoAnterior", "proximoPasso"]);
const props = defineProps({
	requisitosForm: Object,
	dataVaga: Object,
	formDesabilitado: Boolean,
	escondeBotoes: Boolean,
	modoSolicitacao: Boolean,
});

const listaComprovacaoCTPS = [
	{ label: "Sim", value: "S" },
	{ label: "Não", value: "N" },
]

const hintObservacoes = "Complemente requisitos necessários (CNH, disponibilidade de horário, disponibilidade para viajar, conhecimentos necessários, etc)"

const listaEscolaridade = ref([]);
const listaHabilitacoes = ref([]);
const listaExperiencia = ref([]);

const loading = ref(false);
const editando = ref(false);
const formRequisitos = ref();
const form = ref({
	id: editando ? Number(route.params?.id) : undefined,
	experiencia: undefined,
	comprovacaoCTPS: undefined,
	escolaridadeMinima: undefined,
	tipoHabilitacao: undefined,
	observacoes: "",
})

async function salvarRequisitos() {
	if (props.formDesabilitado) {
		emit("proximoPasso");
		return;
	}
	await formRequisitos.value.validate();
	if (!formRequisitos.value.isValid) {
		return;
	}
	const bodyRequest = ref({
		id: editando.value ? Number(form.value.id) : undefined,
		stepCadastroVaga: vagasEnum[2],
		requisitos: {
			experiencia: form.value.experiencia ? form.value.experiencia : undefined,
			comprovacaoCTPS: form.value.comprovacaoCTPS,
			escolaridadeMinima: form.value.escolaridadeMinima,
			tipoHabilitacao: form.value.tipoHabilitacao,
			observacoes: form.value.observacoes,
		}
	})
	if (!editando.value) {
		loading.value = true;
		await cadastrarVaga({
				...props.dataVaga,
				...bodyRequest.value,
			})
			.then(({ data }) => {
				emit("proximoPasso");
			})
			.catch((error) => {
				ErroAlert({ mensagem: "Ocorreu um erro ao salvar requisitos da vaga. Tente novamente mais tarde!", error });
			})
			.finally(() => loading.value = false);
	}
	else {
		loading.value = true;
		await editarVaga(bodyRequest.value.id || 0,
			{
				...props.dataVaga,
				...bodyRequest.value,
			})
			.then(({ data }) => {
				emit("proximoPasso");
			})
			.catch((error) => {
				ErroAlert({ mensagem: "Ocorreu um erro ao editar requisitos da vaga. Tente novamente mais tarde!", error });
			})
			.finally(() => loading.value = false);
	}
}

const corTexto = computed(() => {
	return vuetify.theme.current.value.dark ? "white" : "primary";
})

const desabilitaAvancar = computed(() => {
	const ignorarCampos = ["id"]
	const formValido = Object.keys(form.value).every(key => {
		if (ignorarCampos.includes(key)){
			return true;
		}
		return !!form.value[key] && form.value[key] !== "";
	});
	let contemErro = false;
	if (formRequisitos.value) {
		contemErro = !!formRequisitos.value.errors.length
	}
	return !formValido || contemErro;
});

async function buscarEscolaridade() {
	if (store.getters["entidades/getListaEscolaridade"].length) {
		listaEscolaridade.value = store.getters["entidades/getListaEscolaridade"];
		return;
	}
	await listarEscolaridade()
		.then(({ data }) => {
			listaEscolaridade.value = data;
			store.commit("entidades/setListaEscolaridade", data)
		})
		.catch((error) => {
			console.error(error.response.data.mensagem)
		});
}

async function buscarHabilitacoes() {
	if (store.getters["entidades/getListaHabilitacoes"].length) {
		listaHabilitacoes.value = store.getters["entidades/getListaHabilitacoes"];
		if (!form.value.tipoHabilitacao) {
			form.value.tipoHabilitacao = listaHabilitacoes.value.find((h) => h.codigo === "01");
		}
		return;
	}
	await listarHabilitacoes()
		.then(({ data }) => {
			listaHabilitacoes.value = data;
			if (!form.value.tipoHabilitacao) {
				form.value.tipoHabilitacao = listaHabilitacoes.value.find((h) => h.codigo === "01");
			}
			store.commit("entidades/setListaHabilitacoes", data)
		})
		.catch((error) => {
			console.error(error.response.data.mensagem)
		});
}

async function buscarExperiencia() {
	if (store.getters["entidades/getListaExperiencias"].length) {
		listaExperiencia.value = store.getters["entidades/getListaExperiencias"];
		if (!form.value.experiencia || form.value.experiencia.id === null) {
			form.value.experiencia = listaExperiencia.value[0];
		}
		return;
	}
	await listarExperiencias()
		.then(({ data }) => {
			listaExperiencia.value = data;
			store.commit("entidades/setListaExperiencias", data);
			if (!form.value.experiencia || form.value.experiencia.id === null) {
				form.value.experiencia = listaExperiencia.value[0];
			}
		})
		.catch((error) => {
			console.error(error.response.data.mensagem)
		});
}

async function voltarPasso() {
	if (props.formDesabilitado) {
		emit("passoAnterior");
		return;
	}
	ConfirmacaoAlert({
		title: "Deseja voltar?",
		message: "Ao voltar você pode perder as informações não salvas nesse formulário",
		onConfirm: () => emit("passoAnterior"),
		confirmText: "Voltar",
		cancelText: "Cancelar",
		showCancelButton: true,
	});
}

watch(() => props, () => {
	if (props.requisitosForm) {
		form.value = props.requisitosForm;
	}
}, { deep: true, immediate: true })

onMounted(async () => {
	await buscarEscolaridade();
	await buscarHabilitacoes();
	await buscarExperiencia();
	if (!isNaN(route.params?.id)) {
		editando.value = true;
	}
});
</script>
<style lang="css" scoped>
.radio {
	display: inline-flex;
}
.required {
	color: red;
}
</style>